import {
  SIGN_OUT_CAUSE,
  markSignOutCause,
  takePendingSignOutCause,
  trackSignOut
} from '#~/analytics/domains/sign-out-lifecycle.js';
import type { AnalyticsTrackFn } from '#~/analytics/domains/types.js';
import { track as trackAnalytics } from '#~/analytics/track.js';
import type { AnalyticsEvent } from '#~/analytics/types/index.js';
import { onLogout } from '#~/electron/api.js';
import { ElectronAPI } from '#~/electron/index.js';
import { isElectronApp } from '#~/electron/utils.js';
import { GenericError, useAuth0 as useAuth02 } from '@auth0/auth0-react';
// The underlying SDK still accepts onRedirect for older callers.
import type { GetTokenSilentlyOptions, LogoutOptions } from '@auth0/auth0-spa-js';
import { useCallback } from 'react';
import { logoutAndClearResume } from './logout-and-clear-resume.js';
import { authStore, beginSessionEnded, useAuthStore } from './store.js';
import { isSessionEndedError, writeSessionReturn } from './session-return.js';

// Module-level adapter from (name, category, props) → AnalyticsEvent, the same
// shape auth/provider.tsx uses for its auth-journey tracker.
const trackSignOutEvent: AnalyticsTrackFn = (name, category, properties) => {
  void trackAnalytics({ category, name, properties } as AnalyticsEvent);
};

export const useAuth0 = () => {
  const { userInfo, sessionEnded } = useAuthStore();
  const { getAccessTokenSilently, error, user, isAuthenticated, isLoading, logout } = useAuth02();

  const getWebAccessToken = useCallback(
    async (options?: Omit<GetTokenSilentlyOptions, 'detailedResponse'>): Promise<string> => {
      try {
        const token = await getAccessTokenSilently({ ...options, detailedResponse: false });
        // API callers need a bearer token; the SDK resolves undefined on a cache-only
        // miss or once the ID token's session_expiry ceiling has passed.
        if (!token) throw new GenericError('login_required', 'No access token is available');
        return token;
      } catch (failure) {
        if (isSessionEndedError(failure) && isAuthenticated && user?.sub) {
          if (beginSessionEnded(user.sub)) {
            trackSignOut(trackSignOutEvent, { cause: SIGN_OUT_CAUSE.SESSION_ENDED });
          } else {
            markSignOutCause(SIGN_OUT_CAUSE.SESSION_ENDED);
            window.location.assign('/logout');
          }
        }
        throw failure;
      }
    },
    [getAccessTokenSilently, isAuthenticated, user?.sub]
  );

  const logoutWeb = useCallback(
    async (options: LogoutOptions = {}): Promise<void> => {
      let logoutUrl: string | undefined;
      const openUrl = options.openUrl ?? options.onRedirect;
      // Auth0 only clears its React user when openUrl is supplied. Capture the
      // URL so that cleanup finishes even if external navigation fails or stalls.
      await logout({
        ...options,
        openUrl: (url) => {
          logoutUrl = url;
        }
      });

      if (openUrl === false || logoutUrl === undefined) return;

      if (typeof openUrl === 'function') {
        await openUrl(logoutUrl);
      } else {
        window.location.assign(logoutUrl);
      }
    },
    [logout]
  );

  return {
    getAccessTokenSilently: isElectronApp() ? ElectronAPI.getAccessToken : getWebAccessToken,
    // Clear the reload note without bypassing the SDK's local session cleanup.
    logout: (options?: LogoutOptions) => {
      writeSessionReturn(undefined);
      authStore.setState({ sessionEnded: undefined });
      // ENG-4874: the callers that bypass the /logout route (changing email,
      // the verification gate's Log out) still record their sign-out. Latched
      // per document, so the /logout route's own record is not repeated here.
      trackSignOut(trackSignOutEvent, {
        cause: takePendingSignOutCause() ?? SIGN_OUT_CAUSE.USER
      });
      return logoutAndClearResume({
        isElectron: isElectronApp(),
        hasUser: userInfo !== null && userInfo !== undefined,
        options,
        webLogout: logoutWeb,
        electronLogout: () => ElectronAPI.logout(),
        onElectronLoggedOut: onLogout
      });
    },
    isLoading: isElectronApp() ? !userInfo : isLoading,
    isAuthenticated: isElectronApp() ? !!userInfo : isAuthenticated,
    user: isElectronApp() ? userInfo : user,
    error: isElectronApp() || (sessionEnded && isSessionEndedError(error)) ? null : error
  };
};
